import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { UniversalCamera } from '@babylonjs/core/Cameras/universalCamera';
import { SceneInstrumentation } from '@babylonjs/core/Instrumentation/sceneInstrumentation';

import {
  TICK_DT,
  WEAPONS,
  advanceSpread,
  eyeHeight,
  freshSpread,
  horizLen,
  spawnState,
  spreadAngle,
  stepMovement,
  type InputCommand,
  type MovementState,
  type SpreadState,
} from '@fisura/sim';
import { DEFAULT_SERVER_PORT, TEAM_COLOR, TEAM_SPAWN, buildStaticsForTeam } from '@fisura/shared';
import { EntityFlag, GrenadeType, MatchState, ProjectileKind } from '@fisura/protocol';

import { InputController } from './input.js';
import { Hud } from './hud.js';
import { buildWorld } from './world.js';
import { setupLighting } from './lighting.js';
import { CombatSystem } from './combat.js';
import { NetClient } from './net.js';
import { RemoteRenderer } from './remoteRenderer.js';
import { CrosshairController } from './crosshair.js';
import { ViewModel } from './viewmodel.js';
import { CombatHud, type KillSide } from './hudCombat.js';
import { EndBoard } from './endBoard.js';
import { AudioFx } from './audio.js';
import { ProjectileFx } from './projectileFx.js';
import type { NetSimConfig } from './transport.js';

interface NetPreset {
  label: string;
  cfg: NetSimConfig;
}

const NETSIM_PRESETS: NetPreset[] = [
  { label: 'off', cfg: { rttMs: 0, jitterMs: 0, lossPct: 0 } },
  { label: 'good 60/10/0.5%', cfg: { rttMs: 60, jitterMs: 10, lossPct: 0.5 } },
  { label: 'bad 150/40/3%', cfg: { rttMs: 150, jitterMs: 40, lossPct: 3 } },
];

const MAX_FRAME = 0.25;
const HFOV_RAD = 1.05;

const cssColor = (team: number | undefined): string => {
  const c: [number, number, number] =
    team != null ? (TEAM_COLOR[team] ?? [0.8, 0.8, 0.8]) : [0.8, 0.8, 0.8];
  return `rgb(${Math.round(c[0] * 255)},${Math.round(c[1] * 255)},${Math.round(c[2] * 255)})`;
};

/**
 * Game client: server-authoritative, client-predicted. Drives camera + remote
 * humanoids from the snapshot stream, runs the shared weapon-bloom model
 * locally to size the crosshair (the server still owns the bullet), and renders
 * the combat HUD (health bar / ammo / scores / kill feed).
 */
export function startGame(): void {
  const canvas = document.getElementById('render') as HTMLCanvasElement;
  const overlay = document.getElementById('overlay') as HTMLElement;
  const hitmarker = document.getElementById('hitmarker') as HTMLElement;
  const hudEl = document.getElementById('hud') as HTMLElement;

  const engine = new Engine(canvas, true, { stencil: false, antialias: true });
  const scene = new Scene(engine);
  // Dev-only escape hatch so automated checks can inspect the scene graph.
  if (import.meta.env.DEV) (window as unknown as { __scene?: Scene }).__scene = scene;
  scene.skipPointerMovePicking = true;

  const camera = new UniversalCamera('cam', new Vector3(0, 1.6, -10), scene);
  camera.fov = HFOV_RAD;
  camera.minZ = 0.05;
  camera.maxZ = 1000;
  camera.inputs.clear();

  // Warm-dusk lighting + gradient sky (before world build so materials grade).
  setupLighting(scene, camera);

  const world = buildWorld(scene);
  camera.position.set(world.spawn.x, eyeHeight(false), world.spawn.z);

  const instrumentation = new SceneInstrumentation(scene);
  instrumentation.captureFrameTime = true;

  const input = new InputController(canvas, world.spawn.yaw);
  const hud = new Hud(hudEl);
  const combat = new CombatSystem(scene, camera, hitmarker);
  const crosshair = new CrosshairController();
  const viewmodel = new ViewModel(scene, camera);
  const combatHud = new CombatHud();
  const endBoard = new EndBoard();
  const audio = new AudioFx();

  // Stereo pan + distance volume for a world position, relative to our view.
  const panVol = (sx: number, sz: number, falloff: number): { pan: number; vol: number } => {
    const dx = sx - predicted.pos.x;
    const dz = sz - predicted.pos.z;
    const dist = Math.hypot(dx, dz);
    const bearing = Math.atan2(dx, dz) - input.view.yaw;
    return {
      pan: Math.max(-1, Math.min(1, Math.sin(bearing))),
      vol: Math.min(1, 1 / (1 + dist * falloff)),
    };
  };

  const remotes = new RemoteRenderer(scene, (x, z) => {
    const { pan, vol } = panVol(x, z, 0.22); // footsteps audible within ~15u
    audio.footstep(pan, vol);
  });
  const projectileFx = new ProjectileFx(scene);

  // Production (https via CloudFront) speaks wss on the same host at /ws;
  // local dev talks straight to the game server port.
  const serverUrl =
    location.protocol === 'https:'
      ? `wss://${location.host}/ws`
      : `ws://${location.hostname || 'localhost'}:${DEFAULT_SERVER_PORT}`;

  const presets = [...NETSIM_PRESETS];
  const q = new URLSearchParams(location.search);
  if (q.has('lat')) {
    const cfg: NetSimConfig = {
      rttMs: Number(q.get('lat') ?? 0),
      jitterMs: Number(q.get('jit') ?? 0),
      lossPct: Number(q.get('loss') ?? 0),
    };
    presets.unshift({ label: `url ${cfg.rttMs}/${cfg.jitterMs}/${cfg.lossPct}%`, cfg });
  }
  let presetIdx = 0;
  let simLabel = presets[0]!.label;
  const net = new NetClient(serverUrl, presets[0]!.cfg);

  // Movement statics, swapped to a team-aware variant (enemy spawn zone added as
  // a keep-out) once we know our side, so prediction matches the server.
  let statics = world.statics;
  let staticsTeam = -1;
  let seq = 0;
  let acc = 0;
  let last = performance.now();
  let recoilPitch = 0;

  // Prediction state.
  let predicted: MovementState = spawnState(
    { x: world.spawn.x, y: 0, z: world.spawn.z },
    world.spawn.yaw,
  );
  // Render-interpolation anchor: the position as of the *previous* sim tick. The
  // sim steps at TICK_HZ (30) but we draw at the display refresh (60–144), so we
  // lerp prevPos -> predicted.pos by the leftover accumulator each frame. Without
  // it the position stair-steps at 30 Hz while mouse-look is smooth per frame —
  // very visible as judder when strafing while turning the other way, because the
  // translation parallax and the rotation add up on screen.
  const prevPos = { x: world.spawn.x, y: 0, z: world.spawn.z };
  const pending: { seq: number; cmd: InputCommand }[] = [];
  const posError = { x: 0, y: 0, z: 0 };
  let smoothEyeY: number | null = null; // smooths stair steps; raw when airborne

  // Combat / weapon state.
  let spread: SpreadState = freshSpread();
  let curWeapon = input.weapon;
  let clientAmmo = WEAPONS[curWeapon]!.magSize;
  let prevReloading = false;
  let prevAlive = true;
  let deadSince = 0;
  let lastThrowMs = -Infinity; // grenade cooldown mirror (server enforces)
  // Remaining grenades this life, indexed by GrenadeType — one of each on
  // spawn, mirrors the server's per-life stock (server is authoritative).
  let nadeStock = [1, 1, 1];
  let focusHeat = 0; // 0..1 tunnel vision from sustained full-auto
  let shake = 0; // camera shake from nearby explosions
  const teamById = new Map<number, number>();

  const sideOf = (id: number): KillSide => ({
    name: id === net.myId ? 'YOU' : net.nameOf(id),
    color: cssColor(id === net.myId ? net.myTeam : teamById.get(id)),
  });
  let showScoreboard = false;

  // --- Pointer lock / overlay UX ----------------------------------------
  // Connecting alone never joins the match — only this click does. So menu
  // tabs are spectators, not standing zombie players.
  const nameInput = document.getElementById('playername') as HTMLInputElement | null;
  if (nameInput) {
    nameInput.value = localStorage.getItem('fisura-name') ?? '';
    // Typing in the box must not trigger join/pointer-lock.
    nameInput.addEventListener('click', (e) => e.stopPropagation());
    nameInput.addEventListener('keydown', (e) => e.stopPropagation());
  }
  const defaultGoText =
    overlay.querySelector('.go')?.textContent ?? '▶ Enter the match';
  let wasReconnecting = false;
  overlay.addEventListener('click', () => {
    if (!net.connected) {
      net.retryNow(); // kick the reconnect; join fires automatically on open
    }
    const name = (nameInput?.value ?? '').trim().slice(0, 16);
    if (name) localStorage.setItem('fisura-name', name);
    audio.resume();
    net.join(name);
    input.requestLock();
  });
  const syncOverlay = (): void => {
    const locked = input.isLocked;
    overlay.classList.toggle('hidden', locked);
    crosshair.setVisible(locked);
    combatHud.setVisible(locked);
  };
  document.addEventListener('pointerlockchange', syncOverlay);

  window.addEventListener('keydown', (e) => {
    if (e.code === 'F3') {
      e.preventDefault();
      hud.toggle();
    } else if (e.code === 'F6') {
      e.preventDefault();
      presetIdx = (presetIdx + 1) % presets.length;
      net.setSim(presets[presetIdx]!.cfg);
      simLabel = presets[presetIdx]!.label;
    } else if (e.code === 'Tab' && input.isLocked) {
      e.preventDefault();
      showScoreboard = true;
    }
  });
  window.addEventListener('keyup', (e) => {
    if (e.code === 'Tab') showScoreboard = false;
  });
  window.addEventListener('resize', () => engine.resize());

  // --- Main loop ---------------------------------------------------------
  engine.runRenderLoop(() => {
    const now = performance.now();
    let frame = (now - last) / 1000;
    last = now;
    if (frame > MAX_FRAME) frame = MAX_FRAME;

    // Once our team is known (or it changes), predict against the team-aware
    // statics so the enemy-spawn keep-out matches the server.
    if (net.joined && net.myTeam !== staticsTeam) {
      staticsTeam = net.myTeam;
      statics = buildStaticsForTeam(staticsTeam);
    }

    // Reconcile to the latest authoritative snapshot.
    const recon = net.consumeReconciliation();
    if (recon) {
      clientAmmo = net.selfAmmo; // server owns ammo; local decrements only bridge snapshots
      const e = recon.entity;
      while (pending.length && pending[0]!.seq <= recon.ackSeq) pending.shift();
      const bx = predicted.pos.x;
      const by = predicted.pos.y;
      const bz = predicted.pos.z;
      let s: MovementState = {
        pos: { x: e.x, y: e.y, z: e.z },
        vel: { x: e.vx, y: e.vy, z: e.vz },
        yaw: e.yaw,
        pitch: predicted.pitch,
        onGround: (e.flags & EntityFlag.OnGround) !== 0,
        crouching: (e.flags & EntityFlag.Crouching) !== 0,
      };
      for (const p of pending) s = stepMovement(s, p.cmd, TICK_DT, statics);
      predicted = s;
      const ex = bx - predicted.pos.x;
      const ey = by - predicted.pos.y;
      const ez = bz - predicted.pos.z;
      // Small mispredictions blend smoothly; big ones (teleports, geometry
      // disagreements) snap hard — gliding a large error drags the camera
      // through walls.
      if (Math.hypot(ex, ey, ez) < 1.5) {
        posError.x += ex;
        posError.y += ey;
        posError.z += ez;
        // Shift the interpolation anchor by the same correction the replay just
        // applied to `predicted`, so the prev->cur lerp segment stays continuous
        // across the reconcile (no sub-tick pop). delta applied = -error.
        prevPos.x -= ex;
        prevPos.y -= ey;
        prevPos.z -= ez;
      } else {
        posError.x = 0;
        posError.y = 0;
        posError.z = 0;
        // Hard snap (teleport / geometry disagreement): don't interpolate across
        // it — collapse the anchor onto the corrected position.
        prevPos.x = predicted.pos.x;
        prevPos.y = predicted.pos.y;
        prevPos.z = predicted.pos.z;
      }
    }

    // A fresh Welcome (first join or auto-rejoin): old pending inputs belong
    // to the previous session — replaying them on top of the new spawn state
    // would teleport us. Also (re)show which side we're on.
    if (net.consumeRejoin()) {
      pending.length = 0;
      seq = 0;
      nadeStock = [1, 1, 1]; // a fresh life starts fully stocked
      // Face out of our yard toward the enemy (the camera was seeded with team
      // 0's facing; align it to whichever side we actually landed on).
      input.setView(TEAM_SPAWN[net.myTeam]?.yaw ?? input.view.yaw, 0);
      combatHud.setTeam(net.myTeam);
      combatHud.showTeamIntro(net.myTeam);
    }

    // Fixed-step input sampling + send + predict (only once we've joined).
    acc += frame;
    while (acc >= TICK_DT) {
      if (net.joined) {
        const cmd = input.sample(seq);
        // Outside Playing (3-2-1 countdown / results board) you can look but
        // not move — mirror the server's freeze so prediction stays in sync.
        if (net.match && net.match.state !== MatchState.Playing) cmd.buttons = 0;
        net.sendInput(cmd.seq, cmd.buttons, cmd.yaw, cmd.pitch, cmd.weaponId);
        // Anchor before stepping so we can interpolate prev -> latest below.
        prevPos.x = predicted.pos.x;
        prevPos.y = predicted.pos.y;
        prevPos.z = predicted.pos.z;
        predicted = stepMovement(predicted, cmd, TICK_DT, statics);
        pending.push({ seq, cmd });
        if (pending.length > 256) pending.shift();
        seq++;
      }
      acc -= TICK_DT;
    }

    const decay = Math.exp(-frame * 12);
    posError.x *= decay;
    posError.y *= decay;
    posError.z *= decay;

    // Remote humanoids (teammates get floating name tags).
    const rendered = net.sampleEntities();
    for (const e of rendered) teamById.set(e.id, e.teamId);
    remotes.update(rendered, net.myId, frame, net.myTeam, (id) => net.nameOf(id));

    // Local camera from prediction. The eye's Y is smoothed while grounded so
    // walking stairs glides instead of popping 0.3 per step; airborne (jumps,
    // falls) and teleports (respawn) track raw.
    // Interpolate between the previous and latest sim tick by the leftover
    // accumulator fraction, so horizontal motion is frame-rate smooth instead of
    // 30 Hz stair-stepped. Rotation stays per-frame (zero added aim latency);
    // this adds ≤1 tick (~33 ms) of position-only latency, which is imperceptible.
    const alpha = acc / TICK_DT; // in [0, 1)
    const ix = prevPos.x + (predicted.pos.x - prevPos.x) * alpha;
    const iy = prevPos.y + (predicted.pos.y - prevPos.y) * alpha;
    const iz = prevPos.z + (predicted.pos.z - prevPos.z) * alpha;

    const eye = eyeHeight(predicted.crouching);
    const rawY = iy + posError.y;
    if (smoothEyeY === null || !predicted.onGround || Math.abs(rawY - smoothEyeY) > 1.5) {
      smoothEyeY = rawY;
    } else {
      smoothEyeY += (rawY - smoothEyeY) * Math.min(1, frame * 14);
    }
    camera.position.set(
      ix + posError.x,
      smoothEyeY + eye,
      iz + posError.z,
    );
    const view = input.view;
    // Suppression sway + explosion shake perturb the camera (small, decaying).
    const sway = net.selfSuppression * 0.004 + shake * 0.012;
    const swayP = sway > 0.0001 ? (Math.random() - 0.5) * sway : 0;
    const swayY = sway > 0.0001 ? (Math.random() - 0.5) * sway : 0;
    shake = Math.max(0, shake - frame * 2.5);
    camera.rotation.set(-(view.pitch + recoilPitch) + swayP, view.yaw + swayY, 0);

    // --- Weapons / firing -------------------------------------------------
    const weapon = WEAPONS[input.weapon] ?? WEAPONS[0]!;
    if (input.weapon !== curWeapon) {
      curWeapon = input.weapon;
      clientAmmo = weapon.magSize; // mirror; server is authoritative
    }
    viewmodel.setWeapon(curWeapon);

    const playing = !net.match || net.match.state === MatchState.Playing;
    const alive = net.selfAlive && net.joined;
    const canFire = alive && playing && clientAmmo > 0 && !net.selfReloading;
    const tapped = input.dequeueTap();
    const shots = combat.tick(
      frame * 1000,
      input.isFiring,
      input.isLocked && canFire,
      canFire && tapped,
      weapon,
    );
    if (shots > 0 && canFire) {
      const fwd = camera.getForwardRay(1).direction;
      net.sendFire(seq, Math.max(0, net.serverTick - 2), fwd.x, fwd.y, fwd.z, curWeapon);
      viewmodel.kick();
      audio.shot(curWeapon);
      clientAmmo = Math.max(0, clientAmmo - 1);
    }
    recoilPitch += shots * 0.012;
    recoilPitch = Math.max(0, recoilPitch - frame * 0.12);

    // Grenade throw (G) of the selected type (cycle with B). Throw power scales
    // with look pitch server-side. Client mirrors the per-life stock so the HUD
    // can grey out used ones; the server is authoritative on both stock + power.
    const nadeSel = input.grenade;
    if (
      input.dequeueThrow() &&
      alive &&
      playing &&
      now - lastThrowMs >= 700 &&
      (nadeStock[nadeSel] ?? 0) > 0
    ) {
      lastThrowMs = now;
      nadeStock[nadeSel] = (nadeStock[nadeSel] ?? 0) - 1;
      const fwd = camera.getForwardRay(1).direction;
      net.sendThrow(seq, fwd.x, fwd.y, fwd.z, nadeSel);
      viewmodel.kick();
    }

    // Sustained-fire focus heat: builds while the trigger is held, fades fast.
    focusHeat =
      input.isFiring && canFire
        ? Math.min(1, focusHeat + frame * 0.5)
        : Math.max(0, focusHeat - frame * 1.6);

    // Mirror the server bloom for the crosshair (cosmetic; server owns bullets).
    const aiming = input.isAiming && alive;
    spread = advanceSpread(spread, predicted, shots > 0, weapon, frame, net.selfSuppression);
    crosshair.update(spreadAngle(spread, weapon, aiming), camera.fov, window.innerHeight);

    // ADS zoom: ease the FOV toward the aimed value.
    const targetFov = aiming ? 0.78 : 1.05;
    camera.fov += (targetFov - camera.fov) * Math.min(1, frame * 12);

    // Reload audio on the server flag's rising edge (ammo itself comes from
    // the snapshot, so tactical reloads and auto-reloads just show up).
    if (!prevReloading && net.selfReloading) audio.reload();
    prevReloading = net.selfReloading;

    const moving = horizLen(predicted.vel) > 0.4;
    viewmodel.update(frame, moving, aiming);

    // Other players' gunfire: positional audio + tracer + muzzle flash.
    for (const s of net.consumeShots()) {
      const { pan, vol } = panVol(s.x, s.z, 0.035); // gunfire carries far
      audio.remoteShot(s.weaponId, pan, vol);
      combat.remoteShot(s.x, s.y, s.z, s.dx, s.dy, s.dz, WEAPONS[s.weaponId]?.range ?? 100);
    }

    // Live projectiles + explosions (smoke drifts with the current wind).
    projectileFx.setWind(net.windDir, net.windSpeed);
    projectileFx.update(net.sampleProjectiles());
    for (const ex of net.consumeExplosions()) {
      projectileFx.explode(ex.x, ex.y, ex.z, ex.kind, ex.gtype);
      const { pan, vol } = panVol(ex.x, ex.z, 0.02); // explosions carry very far
      const isSmoke = ex.kind === ProjectileKind.Grenade && ex.gtype === GrenadeType.Smoke;
      const isFlash = ex.kind === ProjectileKind.Grenade && ex.gtype === GrenadeType.Flashbang;
      // Smoke deploys quietly; the flash pops; frag/rockets boom and shake.
      if (!isSmoke) audio.explosion(pan, vol);
      if (!isSmoke && !isFlash) {
        const d = Math.hypot(ex.x - predicted.pos.x, ex.z - predicted.pos.z);
        if (d < 18) shake = Math.min(1, shake + (1 - d / 18));
      }
    }

    // Server-confirmed hits + deaths.
    for (const h of net.consumeHits()) {
      combat.markServerHit(h.lethal);
      audio.hit(h.lethal);
    }
    for (const d of net.consumeDeaths()) {
      combatHud.pushKill(
        sideOf(d.killerId),
        sideOf(d.victimId),
        WEAPONS[d.weaponId]?.name ?? '?',
        d.headshot,
        d.friendlyFire,
      );
    }

    // Damage taken: red flash + directional indicator + thud.
    for (const dmg of net.consumeDamaged()) {
      combatHud.showDamage(dmg.dirYaw);
      audio.damaged();
    }

    // Match state: timer, pre-match countdown, end-of-round leaderboard.
    if (net.match) {
      const st = net.match.state;
      // The results board owns the screen during Intermission — hide the live
      // HUD + crosshair so they don't bleed through it. Restored next match.
      const hudOn = input.isLocked && st !== MatchState.Intermission;
      combatHud.setVisible(hudOn);
      crosshair.setVisible(hudOn);
      combatHud.setTimer(net.match.msLeft, net.match.killTarget);
      combatHud.setBanner('', '#fff'); // the old win banner is replaced by the board
      if (st === MatchState.Starting) {
        combatHud.setCountdown(Math.ceil(net.match.msLeft / 1000));
        endBoard.hide();
      } else if (st === MatchState.Intermission) {
        combatHud.setCountdown(null);
        const myName = net.myId !== null ? net.nameOf(net.myId) : null;
        endBoard.show(net.matchSummary, myName, net.roster.length);
        endBoard.updateCountdown(net.match.msLeft);
      } else {
        combatHud.setCountdown(null);
        endBoard.hide();
      }
      if (net.matchChanged) {
        net.matchChanged = false;
        if (st === MatchState.Intermission) {
          audio.matchEnd(net.match.winnerTeam === net.myTeam);
        }
      }
    }

    // Combat HUD.
    if (!alive && prevAlive && net.joined) {
      deadSince = now;
      audio.death();
    }
    if (alive && !prevAlive) {
      audio.respawn();
      nadeStock = [1, 1, 1]; // respawned with a fresh loadout
      // Snap the view to face the enemy yard again, instead of wherever we
      // happened to be looking when we died.
      input.setView(TEAM_SPAWN[net.myTeam]?.yaw ?? input.view.yaw, 0);
    }
    prevAlive = alive;
    combatHud.setHealth(net.selfHealth);
    combatHud.setAmmo(clientAmmo, weapon.magSize, weapon.name, net.selfReloading);
    combatHud.setGrenades(nadeSel, nadeStock);
    combatHud.setScores(net.score.team0, net.score.team1, net.myTeam);
    combatHud.setDead(net.joined && !net.selfAlive, Math.max(0, 3 - (now - deadSince) / 1000));
    combatHud.setScoreboard(showScoreboard && net.joined, net.roster, net.myId);
    combatHud.update(view.yaw, net.selfHealth, net.selfSuppression, focusHeat);
    combatHud.setBlind(net.selfBlind);
    combatHud.setWind(net.windDir, net.windSpeed);

    // Connection status: the transport reconnects forever on its own and
    // re-Hellos as soon as the socket is back — no reload, no click needed.
    // Only an idle kick (server code 4000) waits for a deliberate click.
    {
      const go = overlay.querySelector('.go');
      if (net.idleKicked) {
        overlay.classList.remove('hidden');
        if (go) go.textContent = '💤 Removed for inactivity — click to rejoin';
        if (document.pointerLockElement) document.exitPointerLock();
      } else if (net.reconnecting) {
        overlay.classList.remove('hidden');
        if (go) go.textContent = '⟲ Reconnecting…';
        wasReconnecting = true;
      } else if (wasReconnecting) {
        // Healed: restore the CTA and re-sync overlay visibility to the lock.
        wasReconnecting = false;
        if (go) go.textContent = input.isLocked ? defaultGoText : '✔ Reconnected — click to resume';
        syncOverlay();
      }
    }

    scene.render();

    hud.update(
      {
        fps: engine.getFps(),
        frameMs: frame * 1000,
        drawCalls: instrumentation.drawCallsCounter.current,
        meshes: scene.getActiveMeshes().length,
        posX: predicted.pos.x,
        posY: predicted.pos.y,
        posZ: predicted.pos.z,
        yaw: input.view.yaw,
        speed: horizLen(predicted.vel),
        onGround: predicted.onGround,
        crouching: predicted.crouching,
        locked: input.isLocked,
        connected: net.connected,
        entityId: net.myId,
        players: rendered.length,
        netsim: simLabel,
        hp: net.selfHealth,
        alive: net.selfAlive,
      },
      frame * 1000,
    );
  });
}
